import React, { useState, useEffect } from "react"; // Core React

// Contexts
import { I18nProvider } from "shared/contexts/I18nContext";
import { LoadingProvider } from "shared/contexts/Loading.jsx";
import { SubscriptionProvider } from "shared/contexts/Subscription.jsx";
import { StudentProvider } from "shared/contexts/Student.jsx";

// Components, Pages, Forms (import all the ones used in the main Layout switch)
import Header from "shared/common/Header.jsx";
import Layout from "shared/common/Layout.jsx";
import Toast from "shared/common/Toast.jsx";

// ... and then the App component code ...

// The root component that sets up contexts, manages global states, and handles routing.
const EnrollmentApp = ({ displayToast = () => { }, curPage = "courses", studentId }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(curPage);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [useMockData, setUseMockData] = useState(false);
  const [currentCourseId, setCurrentCourseId] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [currentStudentId, setCurrentStudentId] = useState(studentId);
  const [currentRequestedCourseId, setCurrentRequestedCourseId] =
    useState(null);
  const [currentEvaluationId, setCurrentEvaluationId] = useState(null);
  const [currentEnrollmentId, setCurrentEnrollmentId] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [currentQuizMode, setCurrentQuizMode] = useState(null);

  // User details for the header (mocked for now)
  const userName = "Janki Ram";
  const userRole = "Teacher";
  const profilePictureUrl = ""; // Leave empty to see initials fallback, or add a URL

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const toggleMockMode = () => {
    setUseMockData((prev) => !prev);
    // Reset current page to force data re-fetch with new mode
    setCurrentPage("courses");
    displayToast(
      "Switched to " + (!useMockData ? "Mock Mode" : "Live Mode"),
      "info"
    );
  };

  // Function to navigate between pages and set relevant IDs/Titles for forms/details.
  const navigateTo = (
    page,
    idOrTitle = "",
    secondIdOrTitle = "",
    thirdIdOrTitle = ""
  ) => {
    console.log(
      `[App.js] Navigating to: ${page}, idOrTitle: ${idOrTitle}, secondIdOrTitle: ${secondIdOrTitle}, thirdIdOrTitle: ${thirdIdOrTitle}`
    );
    setCurrentPage(page);
    // Clear previous IDs to ensure clean state for new forms
    setCurrentCourseId(null);
    setCurrentBatchId(null);
    setCurrentStudentId(null);
    setCurrentRequestedCourseId(null);
    setSelectedCourseTitle("");
    setSelectedCourseId(null);
    setSelectedBatchId(null);
    setCurrentEvaluationId(null);
    setCurrentQuizMode(null);
    console.log(page)

    if (page === "updateCourse") {
      setCurrentCourseId(idOrTitle);
    } else if (page === "updateBatch") {
      setSelectedCourseTitle(idOrTitle);
      setSelectedCourseId(secondIdOrTitle);
      setCurrentBatchId(thirdIdOrTitle);
    } else if (page === "myBatches" || page === "courseHub") {
      setSelectedCourseTitle(idOrTitle);
      setSelectedCourseId(secondIdOrTitle);
    } else if (page === "createBatch") {
      setSelectedCourseTitle(idOrTitle);
      setSelectedCourseId(secondIdOrTitle);
    } else if (page === "studentRoster") {
      setSelectedCourseTitle(idOrTitle);
      setSelectedCourseId(secondIdOrTitle);
      setCurrentBatchId(thirdIdOrTitle);
    } else if (page === "studentHub") {
      setCurrentStudentId(idOrTitle);
    } else if (page === "findCourses") {
      setCurrentStudentId(idOrTitle);
      // } else if (page === "createEnrollmentRequest") {
      //   setCurrentStudentId(idOrTitle);
    } else if (page === "createBatchEnrollment") {
      setCurrentStudentId(idOrTitle);
      setCurrentRequestedCourseId(secondIdOrTitle);
      setCurrentEnrollmentId(thirdIdOrTitle);
    } else if (page === "createRequestEnrollment") {
      setCurrentStudentId(idOrTitle);
      setCurrentRequestedCourseId(secondIdOrTitle);
      setCurrentEnrollmentId(thirdIdOrTitle);
    } else if (page === "updateStudent") {
      setCurrentStudentId(idOrTitle);
    } else if (page === "batchEvaluations") {
      setSelectedCourseTitle(idOrTitle);
      setSelectedCourseId(secondIdOrTitle);
      setCurrentBatchId(thirdIdOrTitle);
    } else if (page === "viewAssignments") {
      setCurrentEvaluationId(idOrTitle);
    } else if (page === "createBatchEvaluation") {
      setSelectedBatchId(idOrTitle);
    } else if (page === "assignEvaluationToBatch") {
      setSelectedBatchId(idOrTitle);
      setSelectedCourseTitle(secondIdOrTitle);
      setCurrentEvaluationId(thirdIdOrTitle);
      console.log(
        `[EnrollmentPage] Setting evaluation ID for assignment: ${thirdIdOrTitle}`
      );
    } else if (page === "batchAssignments") {
      setSelectedBatchId(idOrTitle);
      setSelectedCourseTitle(secondIdOrTitle);
    } else if (page === "evaluationAssignments") {
      setSelectedBatchId(idOrTitle);
      setSelectedCourseTitle(secondIdOrTitle);
      setCurrentEvaluationId(thirdIdOrTitle);
      console.log(`[EnrollmentPage] Setting evaluation ID: ${thirdIdOrTitle}`);
    } else if (page === "conceptDetails") {
      setSelectedConcept(idOrTitle)
    } else if (page === "startQuiz") {
      console.log(secondIdOrTitle)
      setCurrentEvaluationId(idOrTitle)
      setCurrentQuizMode(secondIdOrTitle)
    } else if (page === "joinQuiz") {
      setCurrentStudentId(idOrTitle)

    }
  };

  // Apply dark class to html element based on theme state
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100 transition-colors duration-300 flex flex-col">
      {/* Tailwind CSS CDN is loaded here for quick setup. For production, consider a build process. */}
      <script src="https://cdn.tailwindcss.com"></script>

      <I18nProvider>
        <LoadingProvider>
          <SubscriptionProvider>
            <StudentProvider>
              <Layout
                HeaderComponent={Header}
                isDropdownOpen={isDropdownOpen}
                toggleDropdown={toggleDropdown}
                toggleTheme={toggleTheme}
                isDarkMode={isDarkMode}
                currentPage={currentPage}
                navigateTo={navigateTo}
                selectedCourseTitle={selectedCourseTitle}
                selectedCourseId={selectedCourseId}
                selectedBatchId={selectedBatchId}
                useMockData={useMockData}
                toggleMockMode={toggleMockMode}
                userName={userName}
                userRole={userRole}
                profilePictureUrl={profilePictureUrl}
                displayToast={displayToast}
                courseId={currentCourseId}
                batchId={currentBatchId}
                studentId={currentStudentId}
                requestedCourseId={currentRequestedCourseId}
                evaluationId={currentEvaluationId}
                enrollmentId={currentEnrollmentId}
                conceptCode={selectedConcept}
                quizMode={currentQuizMode}
              />
            </StudentProvider>
          </SubscriptionProvider>
        </LoadingProvider>


      </I18nProvider>
    </div>
  );
};

export default EnrollmentApp;
